import { useContext, useMemo, useEffect } from "react";
import {
    ColDef,
    SetFilterValuesFuncParams,
    RowNode,
} from "ag-grid-community";
import { useDispatch, useSelector } from "react-redux";
import { map, filter, uniq, flatten } from "underscore";
import {
    KeywordTagCellRenderer,
    ArrayListPopOverRenderer
} from "@components/cell-renderers/AllCellRenderers";
import AgGridLayout from "@components/wrappers/layouts/AgGridLayout";
import {
    updateKeywordMappingFilterModel
} from "@features/modalSlice";
import { 
    updateKeywordMappingSelectionData,
    updateKeywordMappingAllPagesSelected,
    updateKeywordMappingHeaderSelection
} from "@features/selectedDataSlice";
import {
    IMainContext,
    TCommonRowData,
    TClientTags,
    TagsMap,
    IPfmSkuFilterParams,
    GlobalTagRecordType,
    VcpmColumns,
    Features,
    ITagMappingStats,
    KwdColumns
} from "@ts/common-types";
import {
    mainContext,
    TAGS_MODULE_MAPPING,
    TAG_COLORS,
    customFilterOptions
} from "@ts/constants";
import {
    getSortedTagTypeList,
    getActiveModule,
    getKwdColFilterValues,
    getKeywordMappingStats,
    getKeywordMappingList,
    numericValueFormatter,
} from "@ts/utils";
import KeywordTagCellEditor from "@components/cell-renderers/KeywordTagCellEditor";
import { useGetBulkActionsMenuData } from "@hooks/useGetBulkActionsMenuData";
import { useMappingDataSource } from '@hooks/AllCustomHooks';
import MappingHeader from "./MappingHeader";
import BulkKeywordMappingModal from "@components/modals/BulkKeywordMappingModal";
import SelectAllHeader from "@components/misc/SelectAllHeader";
import SelectionBanner from "@components/misc/SelectionBanner";

const KeywordMapping = ({
    tagsMappingStats = { top_line: 0, table_data_visibility: [], table_data_upd: [] },
    setTagsMappingStats = (stats: ITagMappingStats) => { /* Not used */ },
    // showPfmTagsStatusModal,
    setShowPfmTagsStatusModal,
    clientIdOnChannelChange
}: {
    tagsMappingStats?: ITagMappingStats;
    setTagsMappingStats?: (stats: ITagMappingStats) => void;
    // showPfmTagsStatusModal: boolean;
    setShowPfmTagsStatusModal: (show: boolean) => void;
    clientIdOnChannelChange: string;
}) => {
    const {
        keywordMappingGridApi,
        setKeywordMappingGridApi,
        setKeywordMappingGridParams,
        keywordMappingGridLoading,
        setKeywordMappingGridLoading,
    } = useContext<IMainContext>(mainContext);

    const dispatch = useDispatch();

    const keywordMappingSelectionData = useSelector(
        (state: { keywordMappingSelectionData: { value: TCommonRowData } }) =>
            state.keywordMappingSelectionData.value
    );

    const keywordMappingAllPagesSelected = useSelector(
        (state: { keywordMappingAllPagesSelected: { value: boolean } }) =>
            state.keywordMappingAllPagesSelected.value
    );

    const keywordMappingHeaderSelection = useSelector(
        (state: { keywordMappingHeaderSelection: { value: boolean } }) =>
            state.keywordMappingHeaderSelection.value
    );

    const bulkKeywordMappingCount = useSelector(
        (state: { bulkKeywordMappingCount: { value: number } }) =>
            state.bulkKeywordMappingCount.value
    );

    const tagTypeList = useSelector(
        (state: { tagTypeList: { value: TClientTags[] } }) =>
            state.tagTypeList.value
    );

    const { activeModule } = getActiveModule();
    const { recordType } = TAGS_MODULE_MAPPING[activeModule as keyof TagsMap];
    const sortedTagTypeList = useMemo(
        () => getSortedTagTypeList(tagTypeList, recordType),
        [tagTypeList, recordType]
    );

    const keywordMappingFilterModel = useSelector(
        (state: {
            keywordMappingFilterModel: { value: IPfmSkuFilterParams };
        }) => state.keywordMappingFilterModel.value
    );

    const showBulkKeywordMappingModal = useSelector(
        (state: { bulkKeywordMappingModalDisplay: { value: boolean } }) =>
            state.bulkKeywordMappingModalDisplay.value
    );

    const showApply1DSKeywordMappingModal = useSelector(
        (state: { apply1DsKeywordMappingModalDisplay: { value: boolean } }) =>
            state.apply1DsKeywordMappingModalDisplay.value
    );

    const bulkActionData = useGetBulkActionsMenuData();
    const filteredBulkActionData =
        Object.keys(keywordMappingFilterModel).length ||
            keywordMappingSelectionData.length
            ? bulkActionData
            : bulkActionData.filter(
                (item) => item.type !== Features.ADD_MAPPING
            );

    const getAvlTagsData = (colField: string) => {
        const result = uniq(
            flatten(
                map(
                    filter(
                        sortedTagTypeList,
                        (tag) =>
                        tag.record_type === GlobalTagRecordType.GLOBAL &&
                        tag.tag_type.toLowerCase() === colField
                    ),
                    (tagType) =>
                        tagType.tags_map.map((tag) => {
                            const [tagId, tagName] = tag.split("##");
                            return {
                                tagId: tagId,
                                tagName: tagName,                                
                            };
                        })
                )
            )
        );
        return result;
    }

    const onKeywordMappingSelectionChanged = () => {
        dispatch(updateKeywordMappingSelectionData(keywordMappingGridApi.getSelectedNodes().filter((node) => node.displayed).map((node) => node.data)));
        
        if (keywordMappingAllPagesSelected) {
            dispatch(updateKeywordMappingAllPagesSelected(false));
        }
        
        const selectedNodes = keywordMappingGridApi.getSelectedNodes().filter((node) => node.displayed);
        if (keywordMappingHeaderSelection && selectedNodes.length === 0) {
            dispatch(updateKeywordMappingHeaderSelection(false));
        }
    };

    const handleSelectAllCurrentPage = () => {
        if (!keywordMappingGridApi) return;
        
        dispatch(updateKeywordMappingHeaderSelection(true));
        
        const pageSize = keywordMappingGridApi.paginationGetPageSize();
        const currentPage = keywordMappingGridApi.paginationGetCurrentPage();
        const startIndex = currentPage * pageSize;
        const endIndex = startIndex + pageSize;
        
        let currentIndex = 0;
        keywordMappingGridApi.forEachNode((rowNode: RowNode) => {
            if (rowNode.displayed) {
                // Only select nodes within current page range
                if (currentIndex >= startIndex && currentIndex < endIndex) {
                    rowNode.setSelected(true);
                }
                currentIndex++;
            }
        });
        
        dispatch(updateKeywordMappingAllPagesSelected(false));
    };

    const handleDeselectAll = () => {
        if (!keywordMappingGridApi) return;
        
        keywordMappingGridApi.deselectAll();
        dispatch(updateKeywordMappingSelectionData([]));
        dispatch(updateKeywordMappingAllPagesSelected(false));
        dispatch(updateKeywordMappingHeaderSelection(false));
    };

    const handleSelectAllPages = () => {
        dispatch(updateKeywordMappingAllPagesSelected(true));
    };

    const handleClearSelection = () => {
        handleDeselectAll();
    };

    useEffect(() => {
        if (keywordMappingGridApi) {
            const handleFilterChanged = () => {
                const filterModel = keywordMappingGridApi.getFilterModel();
                dispatch(updateKeywordMappingFilterModel(filterModel || {}));
            };

            keywordMappingGridApi.addEventListener(
                "filterChanged",
                handleFilterChanged
            );

            return () => {
                keywordMappingGridApi.removeEventListener(
                    "filterChanged",
                    handleFilterChanged
                );
            };
        }
        return () => {
            dispatch(updateKeywordMappingSelectionData([]));
            dispatch(updateKeywordMappingFilterModel({}));
        }
    }, [keywordMappingGridApi, dispatch]);

    const getColumnFilterValues = (
        params: SetFilterValuesFuncParams,
        recordField: string
    ) => {
        const filterModel = keywordMappingGridApi?.getFilterModel() || {};
        const { [recordField]: _, ...restFilters } = filterModel;
        
        getKwdColFilterValues({
            filterModel: restFilters,
            recordField,
        })
            .then((response: any) => {
                if (response) {
                    params.success(
                        [
                            KwdColumns.CATEGORY,
                            KwdColumns.SUBCATEGORY,
                            KwdColumns.BRAND,
                            KwdColumns.CATEGORY_1DS,
                            KwdColumns.SUBCATEGORY_1DS,
                            KwdColumns.BRAND_1DS
                        ].includes(recordField as KwdColumns)
                            ? flatten(response)
                            : response
                    );
                } else {
                    params.success([]);
                }
            })
            .catch((error: any) => {
                console.error(`Error fetching ${recordField} list:`, error);
                params.success([]);
            });
    };

    const keywordMappingDataSource = useMappingDataSource({
        setGridParams: setKeywordMappingGridParams,
        setGridLoading: setKeywordMappingGridLoading,
        setTagsMappingStats,
        getMappingStats: getKeywordMappingStats,
        getMappingList: getKeywordMappingList,
    });

    useEffect(() => {
        if (keywordMappingGridApi) {
            keywordMappingGridApi.refreshServerSideStore({ purge: true });
        }
    }, [clientIdOnChannelChange]);

    const columnDefs = useMemo<ColDef[]>(
        () => [
            {
                field: "keyword",
                headerName: "Keyword",
                filter: "agTextColumnFilter",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                checkboxSelection: true,
                headerComponent: SelectAllHeader,
                headerComponentParams: {
                    onSelectAllCurrentPage: handleSelectAllCurrentPage,
                    onDeselectAll: handleDeselectAll,
                },
                tooltipField: "keyword",
                sortable: true,
                minWidth: 500,
                maxWidth: 700,
                filterParams: {
                    filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                    caseSensitive: false,
                    suppressFilterButton: true,
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, KwdColumns.KEYWORD),
                }
            },
            // Editable Category Column
            {
                field: "category_list",
                headerName: "Category",
                sortable: true,
                editable: true,
                singleClickEdit: true,
                cellRenderer: KeywordTagCellRenderer,
                cellClass: "editable-cell",
                cellEditor: KeywordTagCellEditor,
                cellEditorParams: () => ({
                    avlTagsData: getAvlTagsData(VcpmColumns.CATEGORY.toLowerCase()),
                }),
                valueGetter: (params) => {
                    const data = params.data;
                    if (!data) return [];
                    
                    const categoryList = data.category_list || [];
                    return categoryList.map((cat: { name: string }) => cat.name);
                },
                filter: "agSetColumnFilter",
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, KwdColumns.CATEGORY),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true
                },
                minWidth: 250,
                maxWidth: 260,
                cellStyle: {
                    background: TAG_COLORS[0].background,
                },
            },
            // Editable Subcategory Column
            {
                field: "subcategory_list",
                headerName: "Subcategory",
                sortable: true,
                editable: true,
                singleClickEdit: true,
                cellRenderer: KeywordTagCellRenderer,
                cellClass: "editable-cell",
                cellEditor: KeywordTagCellEditor,
                cellEditorParams: () => ({
                    avlTagsData: getAvlTagsData(VcpmColumns.SUBCATEGORY.toLowerCase()),
                }),
                valueGetter: (params) => {
                    const data = params.data;
                    if (!data) return [];
                    
                    const subcategoryList = data.subcategory_list || [];
                    return subcategoryList.map((cat: { name: string }) => cat.name);
                },
                filter: "agSetColumnFilter",
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, KwdColumns.SUBCATEGORY),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true
                },
                minWidth: 250,
                maxWidth: 260,
                cellStyle: {
                    background: TAG_COLORS[1].background,
                },
            },
            // Editable Brand Column
            {
                field: "brand_list",
                headerName: "Brand",
                sortable: true,
                editable: true,
                singleClickEdit: true,
                cellRenderer: KeywordTagCellRenderer,
                cellClass: "editable-cell",
                cellEditor: KeywordTagCellEditor,
                cellEditorParams: () => ({
                    avlTagsData: getAvlTagsData(VcpmColumns.BRAND.toLowerCase()),
                }),
                valueGetter: (params) => {
                    const data = params.data;
                    if (!data) return [];
                    
                    const brandList = data.brand_list || [];
                    return brandList.map((cat: { name: string }) => cat.name);
                },
                filter: "agSetColumnFilter",
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, KwdColumns.BRAND),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true
                },
                minWidth: 250,
                maxWidth: 260,
                cellStyle: {
                    background: TAG_COLORS[2].background,
                },
            },
            {
                field: "category_list_1ds",
                headerName: "1DS Category",
                sortable: true,
                filter: "agSetColumnFilter",
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, KwdColumns.CATEGORY_1DS),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true
                },
                minWidth: 250,
                maxWidth: 260,
                cellRenderer: ArrayListPopOverRenderer,
                valueGetter: (params) => params.data.category_list_1ds || [],
            },
            {
                field: "subcategory_list_1ds",
                headerName: "1DS Subcategory",
                sortable: true,
                filter: "agSetColumnFilter",
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, KwdColumns.SUBCATEGORY_1DS),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true
                },
                minWidth: 250,
                maxWidth: 260,
                cellRenderer: ArrayListPopOverRenderer,
                valueGetter: (params) => params.data.subcategory_list_1ds || [],
            },
            {
                field: "brand_list_1ds",
                headerName: "1DS Brand",
                sortable: true,
                filter: "agSetColumnFilter",
                filterParams: {
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, KwdColumns.BRAND_1DS),
                    suppressFilterButton: true,
                    refreshValuesOnOpen: true
                },
                minWidth: 250,
                maxWidth: 260,
                cellRenderer: ArrayListPopOverRenderer,
                valueGetter: (params) => params.data.brand_list_1ds || [],
            },
            {
                field: "search_volume",
                headerName: "Search Volume",
                sort: "desc",
                filter: "agNumberColumnFilter",
                valueFormatter: numericValueFormatter,
                minWidth: 150,
                maxWidth: 200,
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                    values: (params: SetFilterValuesFuncParams) =>
                        getColumnFilterValues(params, KwdColumns.SEARCH_VOLUME),
                },
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: false,
                },
            },
        ],
        [sortedTagTypeList, keywordMappingGridApi, keywordMappingSelectionData, keywordMappingAllPagesSelected]
    );

    const getSelectionCount = () => {
        if (keywordMappingAllPagesSelected) {
            return bulkKeywordMappingCount;
        }
        return keywordMappingSelectionData.length;
    };

    return (
        <>
            <div>
                <MappingHeader
                    title="Keyword Mapping"
                    gridApi={keywordMappingGridApi}
                    gridLoading={keywordMappingGridLoading}
                    filterModel={keywordMappingFilterModel}
                    tagsMappingStats={tagsMappingStats}
                    bulkActionData={filteredBulkActionData}
                    onFilterModelUpdate={updateKeywordMappingFilterModel}
                    onShowStatusModal={() => setShowPfmTagsStatusModal(true)}
                    isRefreshEnabled={false}
                />

                <SelectionBanner
                    selectedCount={getSelectionCount()}
                    totalCount={bulkKeywordMappingCount}
                    isAllPagesSelected={keywordMappingAllPagesSelected}
                    isHeaderSelection={keywordMappingHeaderSelection}
                    onSelectAllPages={handleSelectAllPages}
                    onClearSelection={handleClearSelection}
                    itemType="Keywords"
                />

                <div className="border-top pt-2 mt-2" />
                <div
                    id="keyword-mapping-grid"
                    className="ag-theme-alpine product-mapping-grid"
                    style={{
                        height: keywordMappingHeaderSelection 
                            ? "calc(100vh - 300px)" 
                            : "calc(100vh - 250px)", 
                        width: "100%",
                        flex: "1",
                    }}
                >
                    <AgGridLayout
                        api={keywordMappingGridApi}
                        loading={keywordMappingGridLoading}
                        onSelectionChanged={onKeywordMappingSelectionChanged}
                        setGridApi={setKeywordMappingGridApi}
                        columnDefs={columnDefs}
                        isRowSelectable={() => true}
                        rowHeight={48}
                        pagination={true}
                        rowModelType="serverSide"
                        serverSideDatasource={keywordMappingDataSource}
                        serverSideStoreType="partial"
                        loadingCellRenderer={() => null}
                        paginationPageSize={500}
                        cacheBlockSize={2500}
                        maxBlocksInCache={5}
                        isServerSideGrid={true}
                    />
                </div>
            </div>
            {showBulkKeywordMappingModal && <BulkKeywordMappingModal show={showBulkKeywordMappingModal} />}
        </>
    );
};

export default KeywordMapping;
